import Phaser from 'phaser';
import {
  isNumericOrderSolved,
  isSolved,
  moveTile,
  ordersMatch,
  scrambleBoard,
  swapEvidence,
  swapOrder,
} from '../puzzle';
import type {
  Difference,
  EvidenceItem,
  EvidenceOrderLevel,
  HotspotSequenceLevel,
  LevelDefinition,
  PhotoCodeLevel,
  PhotoSequenceLevel,
  ScrollQuizLevel,
  ScrollHotspotLevel,
  SlidingPuzzleLevel,
  SpotDifferenceLevel,
  VerticalStripLevel,
} from '../types';

export interface PuzzleCallbacks {
  onMove: (moves: number) => void;
  onStatus: (message: string, tone?: 'normal' | 'good' | 'bad') => void;
  onComplete: () => void;
  onError: (message: string) => void;
}

type ImageRect = { x: number; y: number; width: number; height: number };

const FONT = '"Microsoft YaHei", "PingFang SC", sans-serif';

export class PuzzleScene extends Phaser.Scene {
  private readonly level: LevelDefinition;
  private readonly callbacks: PuzzleCallbacks;
  private readonly textureKey: string;
  private moves = 0;
  private completed = false;
  private foundDifferences = new Set<string>();
  private board: number[] = [];
  private tileImages = new Map<number, Phaser.GameObjects.Image>();
  private tileBoardRect: ImageRect | null = null;
  private currentHotspotStep = 0;
  private hotspotRect: ImageRect | null = null;
  private hotspotImage: Phaser.GameObjects.Image | null = null;
  private evidenceOrder: string[] = [];
  private evidenceContainers = new Map<string, Phaser.GameObjects.Container>();
  private evidenceSlots: Phaser.Math.Vector2[] = [];
  private focusedIndex = 0;
  private scrollImage: Phaser.GameObjects.Image | null = null;
  private scrollViewport: ImageRect | null = null;
  private scrollImageHeight = 0;
  private scrollOffset = 0;
  private scrollThumb: Phaser.GameObjects.Rectangle | null = null;
  private stripOrder: number[] = [];
  private stripImages = new Map<number, Phaser.GameObjects.Image>();
  private stripViewport: ImageRect | null = null;
  private stripImageHeight = 0;
  private stripOffset = 0;
  private stripThumb: Phaser.GameObjects.Rectangle | null = null;
  private selectedStripSlot = -1;
  private sequenceOrder: number[] = [];
  private sequenceImages = new Map<number, Phaser.GameObjects.Image>();
  private selectedSequenceSlot = -1;
  private quizQuestionIndex = 0;
  private quizFocusedOption = 0;
  private quizAdvancing = false;
  private quizQuestionText: Phaser.GameObjects.Text | null = null;
  private quizCounterText: Phaser.GameObjects.Text | null = null;
  private quizOptionObjects: Phaser.GameObjects.Container[] = [];
  private codeEntry = '';
  private codeDisplayText: Phaser.GameObjects.Text | null = null;
  private codeHintIndex = 0;

  constructor(level: LevelDefinition, callbacks: PuzzleCallbacks) {
    super({ key: `puzzle-${level.id}` });
    this.level = level;
    this.callbacks = callbacks;
    this.textureKey = `scene-${level.id}`;
  }

  preload(): void {
    this.load.on('loaderror', () => this.callbacks.onError(`无法载入「${this.level.title}」的场景图片。`));
    this.load.image(this.textureKey, this.level.puzzle.image);
  }

  create(): void {
    this.cameras.main.setBackgroundColor('#07121b');
    this.add.rectangle(this.scale.width / 2, this.scale.height / 2, this.scale.width, this.scale.height, 0x07121b);
    this.add.rectangle(this.scale.width / 2, 3, this.scale.width * 0.9, 2, 0xb99655, 0.45);
    switch (this.level.kind) {
      case 'spot-difference':
        this.createSpotDifference(this.level);
        break;
      case 'sliding-puzzle':
        this.createSlidingPuzzle(this.level);
        break;
      case 'hotspot-sequence':
        this.createHotspotSequence(this.level);
        break;
      case 'evidence-order':
        this.createEvidenceOrder(this.level);
        break;
      case 'scroll-hotspot':
        this.createScrollHotspot(this.level);
        break;
      case 'vertical-strip':
        this.createVerticalStrip(this.level);
        break;
      case 'photo-sequence':
        this.createPhotoSequence(this.level);
        break;
      case 'scroll-quiz':
        this.createScrollQuiz(this.level);
        break;
      case 'photo-code':
        this.createPhotoCode(this.level);
        break;
    }
    this.setupKeyboard();
  }

  public showHint(): void {
    if (this.completed) return;
    switch (this.level.kind) {
      case 'spot-difference': {
        const difference = this.level.puzzle.differences.find((item) => !this.foundDifferences.has(item.id));
        if (difference) this.pulseSpotHint(difference);
        break;
      }
      case 'sliding-puzzle':
        this.showSlidingPreview();
        break;
      case 'hotspot-sequence': {
        const step = this.level.puzzle.steps[this.currentHotspotStep];
        const hotspot = step ? this.level.puzzle.hotspots.find((item) => item.id === step.hotspotId) : undefined;
        if (hotspot && this.hotspotRect) this.pulseAt(this.hotspotRect, hotspot.x, hotspot.y, hotspot.radius);
        break;
      }
      case 'evidence-order': {
        const level = this.level as EvidenceOrderLevel;
        const wrongIndex = this.evidenceOrder.findIndex((id, index) => id !== level.puzzle.correctOrder[index]);
        const id = wrongIndex >= 0 ? this.evidenceOrder[wrongIndex] : undefined;
        const container = id ? this.evidenceContainers.get(id) : undefined;
        if (container) this.tweens.add({ targets: container, scale: 1.06, duration: 220, yoyo: true, repeat: 2 });
        this.callbacks.onStatus(this.level.puzzle.clue, 'good');
        break;
      }
      case 'scroll-hotspot': {
        const step = this.level.puzzle.steps[this.currentHotspotStep];
        const hotspot = step ? this.level.puzzle.hotspots.find((item) => item.id === step.hotspotId) : undefined;
        if (hotspot) this.revealScrollHint(hotspot.x, hotspot.y, hotspot.radius);
        break;
      }
      case 'vertical-strip': {
        const wrongSlot = this.stripOrder.findIndex((value, index) => value !== index);
        const value = wrongSlot >= 0 ? this.stripOrder[wrongSlot] : undefined;
        const image = value === undefined ? undefined : this.stripImages.get(value);
        if (image) this.tweens.add({ targets: image, alpha: 0.45, duration: 180, yoyo: true, repeat: 3 });
        this.callbacks.onStatus(wrongSlot >= 0 ? `提示：第 ${wrongSlot + 1} 列的碎片还没有归位。` : '所有纵向碎片已经归位。', 'good');
        break;
      }
      case 'photo-sequence': {
        const wrongSlot = this.sequenceOrder.findIndex((value, index) => value !== index);
        const value = wrongSlot >= 0 ? this.sequenceOrder[wrongSlot] : undefined;
        const image = value === undefined ? undefined : this.sequenceImages.get(value);
        if (image) this.tweens.add({ targets: image, scale: 1.04, duration: 180, yoyo: true, repeat: 3 });
        this.callbacks.onStatus(wrongSlot >= 0 ? `提示：位置 ${wrongSlot + 1} 的照片段落还不正确。` : '三段照片已经归位。', 'good');
        break;
      }
      case 'scroll-quiz':
        this.showScrollQuizHint(this.level);
        break;
      case 'photo-code':
        this.showPhotoCodeHint(this.level);
        break;
    }
  }

  public resetPuzzle(): void {
    if (this.completed) return;
    this.moves = 0;
    this.callbacks.onMove(0);
    switch (this.level.kind) {
      case 'spot-difference':
        this.foundDifferences.clear();
        this.scene.restart();
        break;
      case 'sliding-puzzle':
        this.board = scrambleBoard(
          this.level.puzzle.gridSize,
          this.level.puzzle.scrambleMoves,
          this.level.puzzle.seed,
        );
        this.layoutSlidingTiles(this.level);
        this.callbacks.onStatus('拼图已恢复到本关的初始排列。');
        break;
      case 'hotspot-sequence':
        this.currentHotspotStep = 0;
        this.scene.restart();
        break;
      case 'evidence-order':
        this.evidenceOrder = [...this.level.puzzle.initialOrder];
        this.layoutEvidence(this.level);
        this.callbacks.onStatus(this.level.puzzle.clue);
        break;
      case 'scroll-hotspot':
        this.currentHotspotStep = 0;
        this.scene.restart();
        break;
      case 'vertical-strip':
        this.stripOrder = [...this.level.puzzle.initialOrder];
        this.selectedStripSlot = -1;
        this.stripOffset = 0;
        this.layoutVerticalStrips();
        this.callbacks.onStatus('纵向碎条已恢复到初始顺序。');
        break;
      case 'photo-sequence':
        this.sequenceOrder = [...this.level.puzzle.initialOrder];
        this.selectedSequenceSlot = -1;
        this.layoutPhotoSequence(this.level);
        this.callbacks.onStatus('照片段落已恢复到初始顺序。');
        break;
      case 'scroll-quiz':
        this.quizQuestionIndex = 0;
        this.quizFocusedOption = 0;
        this.scene.restart();
        break;
      case 'photo-code':
        this.codeEntry = '';
        this.codeHintIndex = 0;
        this.scene.restart();
        break;
    }
  }

  public solveForTest(): void {
    if (this.completed) return;
    this.moves = Math.max(this.moves, 1);
    this.callbacks.onMove(this.moves);
    this.completePuzzle();
  }

  private setupKeyboard(): void {
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      if (this.completed) return;
      if (this.level.kind === 'sliding-puzzle') this.handleSlidingKeyboard(event, this.level);
      if (this.level.kind === 'evidence-order') this.handleEvidenceKeyboard(event, this.level);
      if (this.level.kind === 'spot-difference' && event.key === 'Enter') {
        const remaining = this.level.puzzle.differences.filter((item) => !this.foundDifferences.has(item.id));
        const selected = remaining[this.focusedIndex % Math.max(remaining.length, 1)];
        if (selected) this.registerDifference(selected);
      }
      if (this.level.kind === 'hotspot-sequence' && event.key === 'Enter') {
        const hotspot = this.level.puzzle.hotspots[this.focusedIndex % this.level.puzzle.hotspots.length];
        if (hotspot) this.registerHotspot(hotspot.id);
      }
      if (this.level.kind === 'scroll-hotspot') this.handleScrollKeyboard(event, this.level);
      if (this.level.kind === 'vertical-strip') this.handleStripKeyboard(event, this.level);
      if (this.level.kind === 'photo-sequence') this.handleSequenceKeyboard(event, this.level);
      if (this.level.kind === 'scroll-quiz') this.handleScrollQuizKeyboard(event, this.level);
      if (this.level.kind === 'photo-code') this.handlePhotoCodeKeyboard(event, this.level);
      if (event.key === 'Tab' && (this.level.kind === 'spot-difference' || this.level.kind === 'hotspot-sequence')) {
        event.preventDefault();
        this.focusedIndex += event.shiftKey ? -1 : 1;
        if (this.focusedIndex < 0) this.focusedIndex = 0;
        this.showHint();
      }
    });
  }

  private createSpotDifference(level: SpotDifferenceLevel): void {
    const portrait = this.scale.height > this.scale.width;
    const panelWidth = portrait ? this.scale.width - 54 : this.scale.width * 0.45;
    const panelHeight = portrait ? this.scale.height * 0.39 : this.scale.height * 0.68;
    const centers = portrait
      ? [new Phaser.Math.Vector2(this.scale.width / 2, this.scale.height * 0.25), new Phaser.Math.Vector2(this.scale.width / 2, this.scale.height * 0.71)]
      : [new Phaser.Math.Vector2(this.scale.width * 0.26, this.scale.height * 0.5), new Phaser.Math.Vector2(this.scale.width * 0.74, this.scale.height * 0.5)];

    const left = this.addFittedImage(centers[0]!, panelWidth, panelHeight);
    const right = this.addFittedImage(centers[1]!, panelWidth, panelHeight);
    this.addFrame(left.rect);
    this.addFrame(right.rect);
    this.addPanelLabel(left.rect, '原始记录');
    this.addPanelLabel(right.rect, '复核影像');

    level.puzzle.differences.forEach((difference) => this.drawDifferenceMark(right.rect, difference));
    for (const panel of [left, right]) {
      panel.image.setInteractive({ useHandCursor: true });
      panel.image.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
        const normalized = this.toNormalized(pointer.worldX, pointer.worldY, panel.rect);
        const hit = level.puzzle.differences.find((difference) => {
          const dx = normalized.x - difference.x;
          const dy = normalized.y - difference.y;
          return Math.hypot(dx, dy) <= difference.radius;
        });
        this.incrementMove();
        if (hit) this.registerDifference(hit);
        else this.flashMiss(pointer.worldX, pointer.worldY);
      });
    }
    this.callbacks.onStatus(`还剩 ${level.puzzle.differences.length} 处不同。`);
  }

  private drawDifferenceMark(rect: ImageRect, difference: Difference): void {
    const x = rect.x + rect.width * difference.x;
    const y = rect.y + rect.height * difference.y;
    const size = Math.max(9, rect.width * difference.radius * 0.38);
    const graphics = this.add.graphics();
    graphics.setDepth(4);
    switch (difference.mark) {
      case 'glow':
        graphics.fillStyle(0xf0c66e, 0.48);
        graphics.fillCircle(x, y, size * 1.7);
        graphics.fillStyle(0xffe6a3, 0.82);
        graphics.fillCircle(x, y, size * 0.55);
        break;
      case 'ring':
        graphics.lineStyle(Math.max(3, size * 0.28), 0x8e2834, 0.9);
        graphics.strokeCircle(x, y, size * 1.35);
        break;
      case 'slash':
        graphics.lineStyle(Math.max(3, size * 0.3), 0xefd48d, 0.9);
        graphics.lineBetween(x - size, y + size, x + size, y - size);
        break;
      case 'diamond':
        graphics.fillStyle(0x8e2834, 0.82);
        graphics.fillTriangle(x, y - size, x + size, y, x, y + size);
        graphics.fillTriangle(x, y - size, x, y + size, x - size, y);
        break;
      case 'dots':
        graphics.fillStyle(0xf0c66e, 0.9);
        graphics.fillCircle(x - size * 0.55, y, size * 0.32);
        graphics.fillCircle(x + size * 0.55, y, size * 0.32);
        break;
    }
  }

  private registerDifference(difference: Difference): void {
    if (this.foundDifferences.has(difference.id)) {
      this.callbacks.onStatus(`「${difference.label}」已经记录过了。`);
      return;
    }
    this.foundDifferences.add(difference.id);
    const remaining = (this.level as SpotDifferenceLevel).puzzle.differences.length - this.foundDifferences.size;
    this.callbacks.onStatus(remaining > 0 ? `找到「${difference.label}」，还剩 ${remaining} 处。` : '所有差异都已归档。', 'good');
    if (remaining === 0) this.completePuzzle();
  }

  private pulseSpotHint(difference: Difference): void {
    const portrait = this.scale.height > this.scale.width;
    const panelWidth = portrait ? this.scale.width - 54 : this.scale.width * 0.45;
    const panelHeight = portrait ? this.scale.height * 0.39 : this.scale.height * 0.68;
    const center = portrait
      ? new Phaser.Math.Vector2(this.scale.width / 2, this.scale.height * 0.71)
      : new Phaser.Math.Vector2(this.scale.width * 0.74, this.scale.height * 0.5);
    const rect = this.calculateFitRect(center, panelWidth, panelHeight);
    this.pulseAt(rect, difference.x, difference.y, difference.radius);
    this.callbacks.onStatus('提示只停留片刻：留意闪动的区域。', 'good');
  }

  private createSlidingPuzzle(level: SlidingPuzzleLevel): void {
    const portrait = this.scale.height > this.scale.width;
    const maxWidth = portrait ? this.scale.width - 70 : this.scale.width * 0.68;
    const maxHeight = portrait ? this.scale.height * 0.72 : this.scale.height * 0.82;
    const source = this.getSourceImage();
    const aspect = source.width / source.height;
    let width = maxWidth;
    let height = width / aspect;
    if (height > maxHeight) {
      height = maxHeight;
      width = height * aspect;
    }
    this.tileBoardRect = {
      x: (this.scale.width - width) / 2,
      y: (this.scale.height - height) / 2,
      width,
      height,
    };
    this.addFrame(this.tileBoardRect, 0x927545);
    this.createTileTextures(level);
    this.board = scrambleBoard(level.puzzle.gridSize, level.puzzle.scrambleMoves, level.puzzle.seed);
    this.layoutSlidingTiles(level);
    this.callbacks.onStatus(`移动相邻图块，恢复 ${level.puzzle.gridSize}×${level.puzzle.gridSize} 影像。`);
  }

  private createTileTextures(level: SlidingPuzzleLevel): void {
    const source = this.getSourceImage();
    const size = level.puzzle.gridSize;
    const cropWidth = source.width / size;
    const cropHeight = source.height / size;
    const blankValue = size * size - 1;
    for (let value = 0; value < blankValue; value += 1) {
      const key = this.tileTextureKey(value);
      const canvasTexture = this.textures.createCanvas(key, Math.ceil(cropWidth), Math.ceil(cropHeight));
      if (!canvasTexture) continue;
      const context = canvasTexture.getContext();
      const column = value % size;
      const row = Math.floor(value / size);
      context.drawImage(source, column * cropWidth, row * cropHeight, cropWidth, cropHeight, 0, 0, cropWidth, cropHeight);
      canvasTexture.refresh();
    }
  }

  private layoutSlidingTiles(level: SlidingPuzzleLevel): void {
    const rect = this.tileBoardRect;
    if (!rect) return;
    const size = level.puzzle.gridSize;
    const blankValue = size * size - 1;
    const tileWidth = rect.width / size;
    const tileHeight = rect.height / size;
    for (const image of this.tileImages.values()) image.destroy();
    this.tileImages.clear();
    this.board.forEach((value, index) => {
      if (value === blankValue) return;
      const column = index % size;
      const row = Math.floor(index / size);
      const image = this.add.image(
        rect.x + column * tileWidth + tileWidth / 2,
        rect.y + row * tileHeight + tileHeight / 2,
        this.tileTextureKey(value),
      );
      image.setDisplaySize(tileWidth - 6, tileHeight - 6);
      image.setInteractive({ useHandCursor: true });
      image.on('pointerdown', () => this.trySlidingMove(value, level));
      this.tileImages.set(value, image);
    });
  }

  private trySlidingMove(value: number, level: SlidingPuzzleLevel): void {
    const index = this.board.indexOf(value);
    const next = moveTile(this.board, index, level.puzzle.gridSize);
    if (!next) {
      const image = this.tileImages.get(value);
      if (image) this.tweens.add({ targets: image, angle: 2, duration: 55, yoyo: true, repeat: 2 });
      this.callbacks.onStatus('这个图块还挨不到空格。', 'bad');
      return;
    }
    this.board = next;
    this.incrementMove();
    this.layoutSlidingTiles(level);
    if (isSolved(this.board)) this.completePuzzle();
    else this.callbacks.onStatus('图块已移动，继续寻找完整画面。');
  }

  private handleSlidingKeyboard(event: KeyboardEvent, level: SlidingPuzzleLevel): void {
    const size = level.puzzle.gridSize;
    const blank = this.board.indexOf(size * size - 1);
    const row = Math.floor(blank / size);
    const column = blank % size;
    let target = -1;
    if (event.key === 'ArrowUp' && row < size - 1) target = blank + size;
    if (event.key === 'ArrowDown' && row > 0) target = blank - size;
    if (event.key === 'ArrowLeft' && column < size - 1) target = blank + 1;
    if (event.key === 'ArrowRight' && column > 0) target = blank - 1;
    if (target >= 0) {
      event.preventDefault();
      const value = this.board[target];
      if (value !== undefined) this.trySlidingMove(value, level);
    }
  }

  private showSlidingPreview(): void {
    if (!this.tileBoardRect) return;
    const rect = this.tileBoardRect;
    const preview = this.add.image(rect.x + rect.width / 2, rect.y + rect.height / 2, this.textureKey);
    preview.setDisplaySize(rect.width, rect.height);
    preview.setDepth(20).setAlpha(0);
    const border = this.add.rectangle(rect.x + rect.width / 2, rect.y + rect.height / 2, rect.width, rect.height);
    border.setStrokeStyle(5, 0xe5c881, 0.9).setDepth(21).setAlpha(0);
    this.tweens.add({
      targets: [preview, border],
      alpha: 1,
      duration: 180,
      hold: 1100,
      yoyo: true,
      onComplete: () => {
        preview.destroy();
        border.destroy();
      },
    });
    this.callbacks.onStatus('原图只会显现一瞬，请记住物件的位置。', 'good');
  }

  private createHotspotSequence(level: HotspotSequenceLevel): void {
    const portrait = this.scale.height > this.scale.width;
    const center = new Phaser.Math.Vector2(this.scale.width / 2, this.scale.height / 2);
    const fitted = this.addFittedImage(center, this.scale.width - (portrait ? 44 : 90), this.scale.height - 80);
    this.hotspotImage = fitted.image;
    this.hotspotRect = fitted.rect;
    this.addFrame(fitted.rect);
    fitted.image.setInteractive({ useHandCursor: true });
    fitted.image.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (!this.hotspotRect) return;
      const normalized = this.toNormalized(pointer.worldX, pointer.worldY, this.hotspotRect);
      const hit = level.puzzle.hotspots.find((hotspot) => Math.hypot(normalized.x - hotspot.x, normalized.y - hotspot.y) <= hotspot.radius);
      this.incrementMove();
      if (hit) this.registerHotspot(hit.id);
      else this.flashMiss(pointer.worldX, pointer.worldY);
    });
    this.callbacks.onStatus(level.puzzle.steps[0]?.prompt ?? '寻找场景中的线索。');
  }

  private registerHotspot(id: string): void {
    const level = this.level as HotspotSequenceLevel;
    const step = level.puzzle.steps[this.currentHotspotStep];
    const hotspot = level.puzzle.hotspots.find((item) => item.id === id);
    if (!step || !hotspot) return;
    if (step.hotspotId !== id) {
      this.callbacks.onStatus(`「${hotspot.label}」不是这句线索指向的物件。`, 'bad');
      return;
    }
    if (this.hotspotRect) this.pulseAt(this.hotspotRect, hotspot.x, hotspot.y, hotspot.radius, 0x78b89a);
    this.currentHotspotStep += 1;
    const nextStep = level.puzzle.steps[this.currentHotspotStep];
    if (!nextStep) this.completePuzzle();
    else this.callbacks.onStatus(`记录正确。下一句：${nextStep.prompt}`, 'good');
  }

  private createScrollHotspot(level: ScrollHotspotLevel): void {
    const portrait = this.scale.height > this.scale.width;
    const viewport: ImageRect = {
      x: portrait ? 34 : 210,
      y: portrait ? 48 : 42,
      width: portrait ? this.scale.width - 78 : 800,
      height: portrait ? this.scale.height - 96 : this.scale.height - 84,
    };
    const source = this.getSourceImage();
    this.scrollViewport = viewport;
    this.scrollImageHeight = viewport.width * source.height / source.width;
    this.scrollOffset = 0;

    this.scrollImage = this.add.image(viewport.x + viewport.width / 2, viewport.y, this.textureKey)
      .setOrigin(0.5, 0)
      .setDisplaySize(viewport.width, this.scrollImageHeight);
    this.addFrame(viewport);
    this.add.text(viewport.x + 14, viewport.y + 12, '按住上下滑动', {
      fontFamily: FONT,
      fontSize: portrait ? '22px' : '18px',
      color: '#f4dfaa',
      backgroundColor: 'rgba(7,18,27,.78)',
      padding: { x: 10, y: 6 },
    }).setDepth(22);
    this.createScrollTrack(viewport, 'hotspot');
    this.setScrollOffset(0);

    const zone = this.add.zone(viewport.x + viewport.width / 2, viewport.y + viewport.height / 2, viewport.width, viewport.height)
      .setInteractive({ useHandCursor: true })
      .setDepth(25);
    let dragStartY = 0;
    let dragStartOffset = 0;
    let dragDistance = 0;
    zone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      dragStartY = pointer.worldY;
      dragStartOffset = this.scrollOffset;
      dragDistance = 0;
    });
    zone.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!pointer.isDown) return;
      const delta = pointer.worldY - dragStartY;
      dragDistance = Math.max(dragDistance, Math.abs(delta));
      this.setScrollOffset(dragStartOffset + delta);
    });
    zone.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (dragDistance > 12) return;
      this.tryScrollHotspot(pointer, level);
    });
    this.input.on('wheel', (pointer: Phaser.Input.Pointer, _objects: unknown[], _dx: number, deltaY: number) => {
      if (this.pointInRect(pointer.worldX, pointer.worldY, viewport)) this.setScrollOffset(this.scrollOffset - deltaY * 0.8);
    });
    this.callbacks.onStatus(level.puzzle.steps[0]?.prompt ?? '上下滑动长卷并寻找线索。');
  }

  private tryScrollHotspot(pointer: Phaser.Input.Pointer, level: ScrollHotspotLevel): void {
    const viewport = this.scrollViewport;
    if (!viewport) return;
    const imageTop = viewport.y + this.scrollOffset;
    const normalizedX = (pointer.worldX - viewport.x) / viewport.width;
    const normalizedY = (pointer.worldY - imageTop) / this.scrollImageHeight;
    const hit = level.puzzle.hotspots.find((hotspot) => {
      const dx = (normalizedX - hotspot.x) * viewport.width;
      const dy = (normalizedY - hotspot.y) * this.scrollImageHeight;
      return Math.hypot(dx, dy) <= viewport.width * hotspot.radius;
    });
    this.incrementMove();
    if (hit) this.registerScrollHotspot(hit.id);
    else this.flashMiss(pointer.worldX, pointer.worldY);
  }

  private registerScrollHotspot(id: string): void {
    const level = this.level as ScrollHotspotLevel;
    const step = level.puzzle.steps[this.currentHotspotStep];
    const hotspot = level.puzzle.hotspots.find((item) => item.id === id);
    if (!step || !hotspot) return;
    if (step.hotspotId !== id) {
      this.callbacks.onStatus(`找到了「${hotspot.label}」，但还没轮到这段记忆。`, 'bad');
      return;
    }
    this.pulseScrollLocation(hotspot.x, hotspot.y, hotspot.radius, 0x78b89a);
    this.currentHotspotStep += 1;
    const next = level.puzzle.steps[this.currentHotspotStep];
    if (!next) this.completePuzzle();
    else this.callbacks.onStatus(`记录正确。下一段：${next.prompt}`, 'good');
  }

  private revealScrollHint(x: number, y: number, radius: number): void {
    const viewport = this.scrollViewport;
    if (!viewport) return;
    const target = viewport.y + viewport.height / 2 - (viewport.y + this.scrollImageHeight * y);
    this.setScrollOffset(target);
    this.time.delayedCall(160, () => this.pulseScrollLocation(x, y, radius));
    this.callbacks.onStatus('长卷已经滑到目标附近，留意闪动的位置。', 'good');
  }

  private pulseScrollLocation(x: number, y: number, radius: number, color = 0xe3bf68): void {
    const viewport = this.scrollViewport;
    if (!viewport) return;
    this.pulseAt({
      x: viewport.x,
      y: viewport.y + this.scrollOffset,
      width: viewport.width,
      height: this.scrollImageHeight,
    }, x, y, radius, color);
  }

  private setScrollOffset(value: number): void {
    const viewport = this.scrollViewport;
    if (!viewport || !this.scrollImage) return;
    const minimum = Math.min(0, viewport.height - this.scrollImageHeight);
    this.scrollOffset = Phaser.Math.Clamp(value, minimum, 0);
    const source = this.getSourceImage();
    const cropY = -this.scrollOffset * source.height / this.scrollImageHeight;
    const cropHeight = Math.min(source.height - cropY, viewport.height * source.height / this.scrollImageHeight);
    this.scrollImage.setCrop(0, cropY, source.width, cropHeight);
    this.scrollImage.y = viewport.y + this.scrollOffset;
    this.updateScrollThumb(this.scrollThumb, viewport, this.scrollImageHeight, this.scrollOffset);
  }

  private createVerticalStrip(level: VerticalStripLevel): void {
    const portrait = this.scale.height > this.scale.width;
    const viewport: ImageRect = {
      x: portrait ? 46 : 250,
      y: portrait ? 48 : 42,
      width: portrait ? this.scale.width - 104 : 720,
      height: portrait ? this.scale.height - 96 : this.scale.height - 84,
    };
    this.stripViewport = viewport;
    this.stripOrder = [...level.puzzle.initialOrder];
    const source = this.getSourceImage();
    const stripSourceWidth = source.width / level.puzzle.stripCount;
    this.stripImageHeight = viewport.width * source.height / source.width;
    this.stripOffset = 0;

    for (let value = 0; value < level.puzzle.stripCount; value += 1) {
      const key = this.stripTextureKey(value);
      const texture = this.textures.createCanvas(key, Math.ceil(stripSourceWidth), source.height);
      if (!texture) continue;
      texture.getContext().drawImage(
        source,
        value * stripSourceWidth,
        0,
        stripSourceWidth,
        source.height,
        0,
        0,
        stripSourceWidth,
        source.height,
      );
      texture.refresh();
      const image = this.add.image(0, viewport.y, key)
        .setOrigin(0.5, 0)
        .setDisplaySize(viewport.width / level.puzzle.stripCount - 3, this.stripImageHeight);
      this.stripImages.set(value, image);
    }
    this.addFrame(viewport);
    this.add.text(viewport.x + 12, viewport.y + 10, '滑动浏览 · 点击两列交换', {
      fontFamily: FONT,
      fontSize: portrait ? '21px' : '17px',
      color: '#f4dfaa',
      backgroundColor: 'rgba(7,18,27,.78)',
      padding: { x: 10, y: 6 },
    }).setDepth(22);
    this.createScrollTrack(viewport, 'strip');
    this.layoutVerticalStrips();

    const zone = this.add.zone(viewport.x + viewport.width / 2, viewport.y + viewport.height / 2, viewport.width, viewport.height)
      .setInteractive({ useHandCursor: true })
      .setDepth(25);
    let startY = 0;
    let startOffset = 0;
    let distance = 0;
    zone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      startY = pointer.worldY;
      startOffset = this.stripOffset;
      distance = 0;
    });
    zone.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!pointer.isDown) return;
      const delta = pointer.worldY - startY;
      distance = Math.max(distance, Math.abs(delta));
      this.setStripOffset(startOffset + delta);
    });
    zone.on('pointerup', (pointer: Phaser.Input.Pointer) => {
      if (distance > 12) return;
      const slot = Math.floor((pointer.worldX - viewport.x) / (viewport.width / level.puzzle.stripCount));
      this.toggleStripSlot(Phaser.Math.Clamp(slot, 0, level.puzzle.stripCount - 1));
    });
    this.input.on('wheel', (pointer: Phaser.Input.Pointer, _objects: unknown[], _dx: number, deltaY: number) => {
      if (this.pointInRect(pointer.worldX, pointer.worldY, viewport)) this.setStripOffset(this.stripOffset - deltaY * 0.8);
    });
    this.callbacks.onStatus('上下滑动检查接缝，点击任意两列即可交换。');
  }

  private layoutVerticalStrips(): void {
    const viewport = this.stripViewport;
    if (!viewport || this.stripOrder.length === 0) return;
    const width = viewport.width / this.stripOrder.length;
    this.stripImages.forEach((image) => image.clearTint().setAlpha(1));
    this.stripOrder.forEach((value, slot) => {
      const image = this.stripImages.get(value);
      if (!image) return;
      const cropY = -this.stripOffset * image.height / this.stripImageHeight;
      const cropHeight = Math.min(image.height - cropY, viewport.height * image.height / this.stripImageHeight);
      image.setCrop(0, cropY, image.width, cropHeight);
      image.x = viewport.x + width * slot + width / 2;
      image.y = viewport.y + this.stripOffset;
      if (slot === this.selectedStripSlot) image.setTint(0xf1d68e).setAlpha(0.82);
    });
    this.updateScrollThumb(this.stripThumb, viewport, this.stripImageHeight, this.stripOffset);
  }

  private setStripOffset(value: number): void {
    const viewport = this.stripViewport;
    if (!viewport) return;
    const minimum = Math.min(0, viewport.height - this.stripImageHeight);
    this.stripOffset = Phaser.Math.Clamp(value, minimum, 0);
    this.layoutVerticalStrips();
  }

  private toggleStripSlot(slot: number): void {
    if (this.selectedStripSlot < 0) {
      this.selectedStripSlot = slot;
      this.layoutVerticalStrips();
      this.callbacks.onStatus(`已选中第 ${slot + 1} 列，再选择另一列交换。`);
      return;
    }
    if (this.selectedStripSlot === slot) {
      this.selectedStripSlot = -1;
      this.layoutVerticalStrips();
      return;
    }
    this.stripOrder = swapOrder(this.stripOrder, this.selectedStripSlot, slot);
    this.selectedStripSlot = -1;
    this.incrementMove();
    this.layoutVerticalStrips();
    if (isNumericOrderSolved(this.stripOrder)) this.completePuzzle();
    else this.callbacks.onStatus('两列已经交换，继续检查照片和文字的接缝。');
  }

  private createPhotoSequence(level: PhotoSequenceLevel): void {
    const source = this.getSourceImage();
    const cropHeight = source.height / level.puzzle.panelCount;
    this.sequenceOrder = [...level.puzzle.initialOrder];
    for (let value = 0; value < level.puzzle.panelCount; value += 1) {
      const key = this.sequenceTextureKey(value);
      const texture = this.textures.createCanvas(key, source.width, Math.ceil(cropHeight));
      if (!texture) continue;
      texture.getContext().drawImage(source, 0, value * cropHeight, source.width, cropHeight, 0, 0, source.width, cropHeight);
      texture.refresh();
      const image = this.add.image(0, 0, key).setInteractive({ useHandCursor: true });
      image.setData('sequenceValue', value);
      image.on('pointerdown', () => {
        const slot = this.sequenceOrder.indexOf(value);
        if (slot >= 0) this.toggleSequenceSlot(slot, level);
      });
      this.sequenceImages.set(value, image);
    }
    this.layoutPhotoSequence(level, true);
    this.callbacks.onStatus('按原图从上到下的顺序，点击两张照片段落进行交换。');
  }

  private layoutPhotoSequence(level: PhotoSequenceLevel, createFrames = false): void {
    const portrait = this.scale.height > this.scale.width;
    const source = this.getSourceImage();
    const segmentAspect = source.width / (source.height / level.puzzle.panelCount);
    const width = portrait ? 580 : 350;
    const height = width / segmentAspect;
    this.sequenceImages.forEach((image) => image.clearTint().setAlpha(1).setDisplaySize(width, height));
    this.sequenceOrder.forEach((value, slot) => {
      const x = portrait ? this.scale.width / 2 : 240 + slot * 380;
      const y = portrait ? 205 + slot * 365 : this.scale.height / 2;
      if (createFrames) {
        this.add.rectangle(x, y, width + 12, height + 12, 0x07121b, 0.9).setStrokeStyle(4, 0xb99655, 0.85);
        this.add.text(x - width / 2 + 10, y - height / 2 + 10, `位置 ${slot + 1}`, {
          fontFamily: FONT,
          fontSize: portrait ? '21px' : '16px',
          color: '#f4dfaa',
          backgroundColor: 'rgba(7,18,27,.78)',
          padding: { x: 8, y: 4 },
        }).setDepth(12);
      }
      const image = this.sequenceImages.get(value);
      if (!image) return;
      image.setPosition(x, y).setDepth(8);
      if (slot === this.selectedSequenceSlot) image.setTint(0xf1d68e).setAlpha(0.78);
    });
  }

  private toggleSequenceSlot(slot: number, level: PhotoSequenceLevel): void {
    if (this.selectedSequenceSlot < 0) {
      this.selectedSequenceSlot = slot;
      this.layoutPhotoSequence(level);
      this.callbacks.onStatus(`已选中位置 ${slot + 1}，再选择另一张交换。`);
      return;
    }
    if (this.selectedSequenceSlot === slot) {
      this.selectedSequenceSlot = -1;
      this.layoutPhotoSequence(level);
      return;
    }
    this.sequenceOrder = swapOrder(this.sequenceOrder, this.selectedSequenceSlot, slot);
    this.selectedSequenceSlot = -1;
    this.incrementMove();
    this.layoutPhotoSequence(level);
    if (isNumericOrderSolved(this.sequenceOrder)) this.completePuzzle();
    else this.callbacks.onStatus('照片段落已经交换，继续恢复长图顺序。');
  }

  private handleScrollKeyboard(event: KeyboardEvent, level: ScrollHotspotLevel): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? -1 : 1;
      this.setScrollOffset(this.scrollOffset + direction * (this.scrollViewport?.height ?? 300) * 0.22);
      return;
    }
    if (event.key === 'Enter') {
      const step = level.puzzle.steps[this.currentHotspotStep];
      if (step) this.registerScrollHotspot(step.hotspotId);
    }
  }

  private handleStripKeyboard(event: KeyboardEvent, level: VerticalStripLevel): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? -1 : 1;
      this.setStripOffset(this.stripOffset + direction * (this.stripViewport?.height ?? 300) * 0.22);
      return;
    }
    if (event.key === 'Tab' || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      const direction = event.shiftKey || event.key === 'ArrowLeft' ? -1 : 1;
      this.focusedIndex = (this.focusedIndex + direction + level.puzzle.stripCount) % level.puzzle.stripCount;
      const value = this.stripOrder[this.focusedIndex];
      const image = value === undefined ? undefined : this.stripImages.get(value);
      if (image) this.tweens.add({ targets: image, alpha: 0.48, duration: 120, yoyo: true, repeat: 1 });
      return;
    }
    if (event.key === 'Enter') this.toggleStripSlot(this.focusedIndex);
  }

  private handleSequenceKeyboard(event: KeyboardEvent, level: PhotoSequenceLevel): void {
    if (event.key === 'Tab' || ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) {
      event.preventDefault();
      const direction = event.shiftKey || event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : 1;
      this.focusedIndex = (this.focusedIndex + direction + level.puzzle.panelCount) % level.puzzle.panelCount;
      const value = this.sequenceOrder[this.focusedIndex];
      const image = value === undefined ? undefined : this.sequenceImages.get(value);
      if (image) this.tweens.add({ targets: image, alpha: 0.48, duration: 120, yoyo: true, repeat: 1 });
      return;
    }
    if (event.key === 'Enter') this.toggleSequenceSlot(this.focusedIndex, level);
  }

  private createScrollQuiz(level: ScrollQuizLevel): void {
    const portrait = this.scale.height > this.scale.width;
    const viewport: ImageRect = portrait
      ? { x: 36, y: 42, width: this.scale.width - 72, height: 700 }
      : { x: 58, y: 42, width: 650, height: this.scale.height - 84 };
    const source = this.getSourceImage();
    this.scrollViewport = viewport;
    this.scrollImageHeight = viewport.width * source.height / source.width;
    this.scrollOffset = 0;
    this.quizQuestionIndex = 0;
    this.quizFocusedOption = 0;
    this.quizAdvancing = false;

    this.scrollImage = this.add.image(viewport.x + viewport.width / 2, viewport.y, this.textureKey)
      .setOrigin(0.5, 0)
      .setDisplaySize(viewport.width, this.scrollImageHeight);
    this.addFrame(viewport);
    this.add.text(viewport.x + 12, viewport.y + 10, '完整长图 · 按住上下滑动', {
      fontFamily: FONT,
      fontSize: portrait ? '21px' : '17px',
      color: '#f4dfaa',
      backgroundColor: 'rgba(7,18,27,.82)',
      padding: { x: 10, y: 6 },
    }).setDepth(22);
    this.createScrollTrack(viewport, 'hotspot');
    this.setScrollOffset(0);

    const zone = this.add.zone(viewport.x + viewport.width / 2, viewport.y + viewport.height / 2, viewport.width, viewport.height)
      .setInteractive({ useHandCursor: true })
      .setDepth(25);
    let dragStartY = 0;
    let dragStartOffset = 0;
    zone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      dragStartY = pointer.worldY;
      dragStartOffset = this.scrollOffset;
    });
    zone.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (!pointer.isDown) return;
      this.setScrollOffset(dragStartOffset + pointer.worldY - dragStartY);
    });
    this.input.on('wheel', (pointer: Phaser.Input.Pointer, _objects: unknown[], _dx: number, deltaY: number) => {
      if (this.pointInRect(pointer.worldX, pointer.worldY, viewport)) this.setScrollOffset(this.scrollOffset - deltaY * 0.8);
    });

    const panel = portrait
      ? { x: 42, y: 790, width: this.scale.width - 84, height: 340 }
      : { x: 752, y: 60, width: 430, height: this.scale.height - 120 };
    this.add.rectangle(panel.x + panel.width / 2, panel.y + panel.height / 2, panel.width, panel.height, 0xd8c79c, 0.98)
      .setStrokeStyle(4, 0x8f7041, 0.95)
      .setDepth(27);
    this.quizCounterText = this.add.text(panel.x + 24, panel.y + 22, '', {
      fontFamily: FONT,
      fontSize: portrait ? '22px' : '18px',
      color: '#7e4d55',
      fontStyle: 'bold',
    }).setDepth(30);
    this.quizQuestionText = this.add.text(panel.x + 24, panel.y + (portrait ? 64 : 72), '', {
      fontFamily: FONT,
      fontSize: portrait ? '27px' : '25px',
      color: '#2c231b',
      fontStyle: 'bold',
      wordWrap: { width: panel.width - 48 },
      lineSpacing: 7,
    }).setDepth(30);
    this.renderScrollQuizQuestion(level, panel);
  }

  private renderScrollQuizQuestion(level: ScrollQuizLevel, panel?: ImageRect): void {
    const portrait = this.scale.height > this.scale.width;
    const resolvedPanel = panel ?? (portrait
      ? { x: 42, y: 790, width: this.scale.width - 84, height: 340 }
      : { x: 752, y: 60, width: 430, height: this.scale.height - 120 });
    const question = level.puzzle.questions[this.quizQuestionIndex];
    if (!question) return;
    this.quizOptionObjects.forEach((object) => object.destroy(true));
    this.quizOptionObjects = [];
    this.quizCounterText?.setText(`观察题 ${this.quizQuestionIndex + 1} / ${level.puzzle.questions.length}`);
    this.quizQuestionText?.setText(question.prompt);
    this.quizFocusedOption = 0;

    const optionWidth = resolvedPanel.width - 48;
    const optionHeight = portrait ? 58 : 62;
    const startY = portrait ? resolvedPanel.y + 180 : resolvedPanel.y + 235;
    question.options.forEach((option, index) => {
      const container = this.add.container(resolvedPanel.x + resolvedPanel.width / 2, startY + index * (optionHeight + 14));
      const background = this.add.rectangle(0, 0, optionWidth, optionHeight, 0x24343b, 1)
        .setStrokeStyle(2, index === 0 ? 0xe4c879 : 0x8f7041, 0.9);
      const label = this.add.text(-optionWidth / 2 + 18, 0, `${String.fromCharCode(65 + index)} · ${option}`, {
        fontFamily: FONT,
        fontSize: portrait ? '23px' : '20px',
        color: '#f5e8c6',
        fontStyle: 'bold',
      }).setOrigin(0, 0.5);
      container.add([background, label]);
      container.setSize(optionWidth, optionHeight);
      container.setInteractive(new Phaser.Geom.Rectangle(-optionWidth / 2, -optionHeight / 2, optionWidth, optionHeight), Phaser.Geom.Rectangle.Contains);
      container.on('pointerdown', () => this.selectScrollQuizOption(index, level, container));
      container.setDepth(32);
      this.quizOptionObjects.push(container);
    });
    this.focusScrollQuizQuestion(question.focusY);
    this.callbacks.onStatus(`第 ${this.quizQuestionIndex + 1} 题：先观察完整长图，再选择答案。`);
  }

  private selectScrollQuizOption(index: number, level: ScrollQuizLevel, container?: Phaser.GameObjects.Container): void {
    if (this.quizAdvancing || this.completed) return;
    const question = level.puzzle.questions[this.quizQuestionIndex];
    if (!question) return;
    this.incrementMove();
    const background = container?.getAt(0) as Phaser.GameObjects.Rectangle | undefined;
    if (index !== question.correctIndex) {
      background?.setFillStyle(0x743d43, 1);
      if (container) this.tweens.add({ targets: container, x: container.x + 7, duration: 55, yoyo: true, repeat: 3 });
      this.callbacks.onStatus('这项与照片不符，再滑动长图看一眼。', 'bad');
      this.time.delayedCall(420, () => background?.setFillStyle(0x24343b, 1));
      return;
    }
    this.quizAdvancing = true;
    background?.setFillStyle(0x4a715b, 1);
    this.callbacks.onStatus(question.success, 'good');
    this.time.delayedCall(520, () => {
      this.quizQuestionIndex += 1;
      this.quizAdvancing = false;
      if (this.quizQuestionIndex >= level.puzzle.questions.length) this.completePuzzle();
      else this.renderScrollQuizQuestion(level);
    });
  }

  private focusScrollQuizQuestion(focusY: number): void {
    const viewport = this.scrollViewport;
    if (!viewport) return;
    const target = viewport.y + viewport.height / 2 - (viewport.y + this.scrollImageHeight * focusY);
    this.setScrollOffset(target);
  }

  private showScrollQuizHint(level: ScrollQuizLevel): void {
    const question = level.puzzle.questions[this.quizQuestionIndex];
    const viewport = this.scrollViewport;
    if (!question || !viewport) return;
    this.focusScrollQuizQuestion(question.focusY);
    this.pulseAt(viewport, 0.5, 0.5, 0.09);
    this.callbacks.onStatus(question.hint, 'good');
  }

  private handleScrollQuizKeyboard(event: KeyboardEvent, level: ScrollQuizLevel): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? -1 : 1;
      this.setScrollOffset(this.scrollOffset + direction * (this.scrollViewport?.height ?? 300) * 0.18);
      return;
    }
    if (event.key === 'Tab') {
      event.preventDefault();
      const question = level.puzzle.questions[this.quizQuestionIndex];
      if (!question) return;
      this.quizFocusedOption = (this.quizFocusedOption + (event.shiftKey ? -1 : 1) + question.options.length) % question.options.length;
      const container = this.quizOptionObjects[this.quizFocusedOption];
      if (container) this.tweens.add({ targets: container, scale: 1.025, duration: 110, yoyo: true });
      return;
    }
    if (event.key === 'Enter') {
      const container = this.quizOptionObjects[this.quizFocusedOption];
      this.selectScrollQuizOption(this.quizFocusedOption, level, container);
    }
  }

  private createPhotoCode(level: PhotoCodeLevel): void {
    const portrait = this.scale.height > this.scale.width;
    const viewport: ImageRect = portrait
      ? { x: 36, y: 28, width: this.scale.width - 72, height: 420 }
      : { x: 58, y: 42, width: 650, height: this.scale.height - 84 };
    const source = this.getSourceImage();
    this.scrollViewport = viewport;
    this.scrollImageHeight = viewport.width * source.height / source.width;
    this.scrollOffset = 0;
    this.codeEntry = '';
    this.codeHintIndex = 0;

    this.scrollImage = this.add.image(viewport.x + viewport.width / 2, viewport.y, this.textureKey)
      .setOrigin(0.5, 0)
      .setDisplaySize(viewport.width, this.scrollImageHeight);
    this.addFrame(viewport);
    this.add.text(viewport.x + 12, viewport.y + 10, '完整长图 · 滑动观察四处细节', {
      fontFamily: FONT,
      fontSize: portrait ? '21px' : '17px',
      color: '#f4dfaa',
      backgroundColor: 'rgba(7,18,27,.82)',
      padding: { x: 10, y: 6 },
    }).setDepth(22);
    this.createScrollTrack(viewport, 'hotspot');
    this.setScrollOffset(0);

    const zone = this.add.zone(viewport.x + viewport.width / 2, viewport.y + viewport.height / 2, viewport.width, viewport.height)
      .setInteractive({ useHandCursor: true })
      .setDepth(25);
    let dragStartY = 0;
    let dragStartOffset = 0;
    zone.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      dragStartY = pointer.worldY;
      dragStartOffset = this.scrollOffset;
    });
    zone.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      if (pointer.isDown) this.setScrollOffset(dragStartOffset + pointer.worldY - dragStartY);
    });
    this.input.on('wheel', (pointer: Phaser.Input.Pointer, _objects: unknown[], _dx: number, deltaY: number) => {
      if (this.pointInRect(pointer.worldX, pointer.worldY, viewport)) this.setScrollOffset(this.scrollOffset - deltaY * 0.8);
    });

    const panel: ImageRect = portrait
      ? { x: 42, y: 488, width: this.scale.width - 84, height: 650 }
      : { x: 752, y: 42, width: 430, height: this.scale.height - 84 };
    this.add.rectangle(panel.x + panel.width / 2, panel.y + panel.height / 2, panel.width, panel.height, 0xd8c79c, 0.98)
      .setStrokeStyle(4, 0x8f7041, 0.95)
      .setDepth(27);
    this.add.text(panel.x + 22, panel.y + 18, '照片密码 · 4 位', {
      fontFamily: FONT,
      fontSize: portrait ? '24px' : '20px',
      color: '#7e4d55',
      fontStyle: 'bold',
    }).setDepth(30);

    const clueStartY = panel.y + (portrait ? 62 : 64);
    const clueSpacing = portrait ? 48 : 55;
    level.puzzle.clues.forEach((clue, index) => {
      const clueText = this.add.text(panel.x + 22, clueStartY + index * clueSpacing, clue.prompt, {
        fontFamily: FONT,
        fontSize: portrait ? '19px' : '16px',
        color: '#2c231b',
        fontStyle: 'bold',
        wordWrap: { width: panel.width - 44 },
        lineSpacing: 2,
      }).setDepth(31).setInteractive({ useHandCursor: true });
      clueText.on('pointerdown', () => {
        this.codeHintIndex = index;
        this.focusPhotoCodeClue(clue.focusY);
        this.callbacks.onStatus(clue.prompt, 'good');
      });
    });

    const displayY = panel.y + (portrait ? 268 : 306);
    this.add.rectangle(panel.x + panel.width / 2, displayY, panel.width - 44, portrait ? 70 : 64, 0x172a33, 1)
      .setStrokeStyle(3, 0xe4c879, 0.9)
      .setDepth(30);
    this.codeDisplayText = this.add.text(panel.x + panel.width / 2, displayY, '', {
      fontFamily: FONT,
      fontSize: portrait ? '34px' : '30px',
      color: '#fff0b4',
      fontStyle: 'bold',
      letterSpacing: 8,
    }).setOrigin(0.5).setDepth(32);
    this.updatePhotoCodeDisplay(level);

    const keys: Array<string> = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '清除', '0', '确认'];
    const columns = portrait ? 4 : 3;
    const rows = Math.ceil(keys.length / columns);
    const horizontalGap = portrait ? 10 : 12;
    const verticalGap = portrait ? 9 : 10;
    const keyWidth = (panel.width - 44 - horizontalGap * (columns - 1)) / columns;
    const keyHeight = portrait ? 78 : 52;
    const keypadStartY = panel.y + (portrait ? 344 : 382);
    const keypadHeight = rows * keyHeight + (rows - 1) * verticalGap;
    keys.forEach((key, index) => {
      const column = index % columns;
      const row = Math.floor(index / columns);
      const x = panel.x + 22 + keyWidth / 2 + column * (keyWidth + horizontalGap);
      const y = keypadStartY + keyHeight / 2 + row * (keyHeight + verticalGap);
      const container = this.add.container(x, y).setDepth(32);
      const isAction = key === '清除' || key === '确认';
      const background = this.add.rectangle(0, 0, keyWidth, keyHeight, isAction ? 0x70434a : 0x24343b, 1)
        .setStrokeStyle(2, key === '确认' ? 0xe4c879 : 0x8f7041, 0.9);
      const label = this.add.text(0, 0, key, {
        fontFamily: FONT,
        fontSize: portrait ? '25px' : '21px',
        color: '#f5e8c6',
        fontStyle: 'bold',
      }).setOrigin(0.5);
      container.add([background, label]);
      container.setSize(keyWidth, keyHeight);
      container.setInteractive(new Phaser.Geom.Rectangle(-keyWidth / 2, -keyHeight / 2, keyWidth, keyHeight), Phaser.Geom.Rectangle.Contains);
      container.on('pointerdown', () => {
        this.tweens.add({ targets: container, scale: 0.94, duration: 70, yoyo: true });
        this.pressPhotoCodeKey(key, level);
      });
    });
    if (keypadStartY + keypadHeight > panel.y + panel.height) {
      this.callbacks.onError('照片密码键盘超出画布范围。');
    }
    this.callbacks.onStatus('数出四条线索的答案，按顺序组成 4 位密码。');
  }

  private updatePhotoCodeDisplay(level: PhotoCodeLevel): void {
    const characters = Array.from({ length: level.puzzle.code.length }, (_, index) => this.codeEntry[index] ?? '—');
    this.codeDisplayText?.setText(characters.join('   '));
  }

  private pressPhotoCodeKey(key: string, level: PhotoCodeLevel): void {
    if (this.completed) return;
    if (key === '清除') {
      this.incrementMove();
      this.codeEntry = '';
      this.updatePhotoCodeDisplay(level);
      this.callbacks.onStatus('密码输入已清除。');
      return;
    }
    if (key === '确认') {
      this.incrementMove();
      if (this.codeEntry.length < level.puzzle.code.length) {
        this.callbacks.onStatus('请先输入完整的 4 位密码。', 'bad');
        return;
      }
      if (this.codeEntry === level.puzzle.code) {
        this.callbacks.onStatus('密码正确，最后一页已经解锁。', 'good');
        this.time.delayedCall(420, () => this.completePuzzle());
        return;
      }
      const correctPositions = Array.from(this.codeEntry).filter((digit, index) => digit === level.puzzle.code[index]).length;
      this.callbacks.onStatus(`密码不正确，其中 ${correctPositions} 位数字的位置正确。重新观察照片。`, 'bad');
      this.codeEntry = '';
      this.updatePhotoCodeDisplay(level);
      this.codeDisplayText && this.tweens.add({ targets: this.codeDisplayText, x: this.codeDisplayText.x + 8, duration: 55, yoyo: true, repeat: 3 });
      return;
    }
    if (!/^\d$/.test(key) || this.codeEntry.length >= level.puzzle.code.length) return;
    this.incrementMove();
    this.codeEntry += key;
    this.updatePhotoCodeDisplay(level);
    this.callbacks.onStatus(this.codeEntry.length === level.puzzle.code.length ? '4 位数字已输入，点击“确认”。' : `已输入第 ${this.codeEntry.length} 位。`);
  }

  private focusPhotoCodeClue(focusY: number): void {
    const viewport = this.scrollViewport;
    if (!viewport) return;
    const target = viewport.y + viewport.height / 2 - (viewport.y + this.scrollImageHeight * focusY);
    this.setScrollOffset(target);
  }

  private showPhotoCodeHint(level: PhotoCodeLevel): void {
    const index = this.codeHintIndex % level.puzzle.clues.length;
    const clue = level.puzzle.clues[index];
    const viewport = this.scrollViewport;
    if (!clue || !viewport) return;
    this.focusPhotoCodeClue(clue.focusY);
    this.pulseAt(viewport, 0.5, 0.5, 0.09);
    this.callbacks.onStatus(`第 ${index + 1} 位提示：${clue.hint}`, 'good');
    this.codeHintIndex = (index + 1) % level.puzzle.clues.length;
  }

  private handlePhotoCodeKeyboard(event: KeyboardEvent, level: PhotoCodeLevel): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const direction = event.key === 'ArrowDown' ? -1 : 1;
      this.setScrollOffset(this.scrollOffset + direction * (this.scrollViewport?.height ?? 300) * 0.18);
      return;
    }
    if (/^\d$/.test(event.key)) {
      event.preventDefault();
      this.pressPhotoCodeKey(event.key, level);
      return;
    }
    if (event.key === 'Backspace' || event.key === 'Delete') {
      event.preventDefault();
      this.codeEntry = this.codeEntry.slice(0, -1);
      this.updatePhotoCodeDisplay(level);
      this.callbacks.onStatus('已删除最后一位数字。');
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this.pressPhotoCodeKey('清除', level);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      this.pressPhotoCodeKey('确认', level);
    }
  }

  private createScrollTrack(viewport: ImageRect, kind: 'hotspot' | 'strip'): void {
    const x = viewport.x + viewport.width + 20;
    this.add.rectangle(x, viewport.y + viewport.height / 2, 5, viewport.height, 0x77613a, 0.38).setDepth(24);
    const thumb = this.add.rectangle(x, viewport.y + 30, 11, 60, 0xe3bf68, 0.9).setDepth(25);
    if (kind === 'hotspot') this.scrollThumb = thumb;
    else this.stripThumb = thumb;
  }

  private updateScrollThumb(
    thumb: Phaser.GameObjects.Rectangle | null,
    viewport: ImageRect,
    imageHeight: number,
    offset: number,
  ): void {
    if (!thumb) return;
    const height = Math.max(48, viewport.height * Math.min(1, viewport.height / imageHeight));
    const minimum = Math.min(0, viewport.height - imageHeight);
    const ratio = minimum === 0 ? 0 : offset / minimum;
    thumb.height = height;
    thumb.y = viewport.y + height / 2 + ratio * (viewport.height - height);
  }

  private pointInRect(x: number, y: number, rect: ImageRect): boolean {
    return x >= rect.x && x <= rect.x + rect.width && y >= rect.y && y <= rect.y + rect.height;
  }

  private createEvidenceOrder(level: EvidenceOrderLevel): void {
    this.evidenceOrder = [...level.puzzle.initialOrder];
    this.createEvidenceTextures(level);
    this.layoutEvidence(level, true);
    this.callbacks.onStatus(level.puzzle.clue);
  }

  private createEvidenceTextures(level: EvidenceOrderLevel): void {
    const source = this.getSourceImage();
    level.puzzle.items.forEach((item) => {
      const key = this.evidenceTextureKey(item.id);
      const crop = item.crop;
      const sourceX = Math.round(source.width * crop.x);
      const sourceY = Math.round(source.height * crop.y);
      const sourceWidth = Math.round(source.width * crop.width);
      const sourceHeight = Math.round(source.height * crop.height);
      const canvasTexture = this.textures.createCanvas(key, sourceWidth, sourceHeight);
      if (!canvasTexture) return;
      canvasTexture.getContext().drawImage(source, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, sourceWidth, sourceHeight);
      canvasTexture.refresh();
    });
  }

  private layoutEvidence(level: EvidenceOrderLevel, create = false): void {
    const portrait = this.scale.height > this.scale.width;
    const cardWidth = portrait ? this.scale.width * 0.72 : this.scale.width * 0.2;
    const cardHeight = portrait ? this.scale.height * 0.17 : this.scale.height * 0.48;
    this.evidenceSlots = this.evidenceOrder.map((_, index) => {
      if (portrait) return new Phaser.Math.Vector2(this.scale.width / 2, this.scale.height * (0.16 + index * 0.225));
      return new Phaser.Math.Vector2(this.scale.width * (0.14 + index * 0.24), this.scale.height * 0.5);
    });

    if (create) {
      level.puzzle.items.forEach((item) => {
        const container = this.createEvidenceCard(item, cardWidth, cardHeight);
        this.evidenceContainers.set(item.id, container);
      });
      this.input.on('drag', (_pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.Container, dragX: number, dragY: number) => {
        gameObject.setPosition(dragX, dragY);
      });
      this.input.on('dragend', (_pointer: Phaser.Input.Pointer, gameObject: Phaser.GameObjects.Container) => {
        const id = String(gameObject.getData('evidenceId'));
        const from = this.evidenceOrder.indexOf(id);
        let closest = 0;
        let distance = Number.POSITIVE_INFINITY;
        this.evidenceSlots.forEach((slot, index) => {
          const candidate = Phaser.Math.Distance.Between(gameObject.x, gameObject.y, slot.x, slot.y);
          if (candidate < distance) {
            closest = index;
            distance = candidate;
          }
        });
        this.evidenceOrder = swapEvidence(this.evidenceOrder, from, closest);
        this.incrementMove();
        this.layoutEvidence(level);
        if (ordersMatch(this.evidenceOrder, level.puzzle.correctOrder)) this.completePuzzle();
      });
    }

    this.evidenceOrder.forEach((id, index) => {
      const container = this.evidenceContainers.get(id);
      const slot = this.evidenceSlots[index];
      if (container && slot) this.tweens.add({ targets: container, x: slot.x, y: slot.y, duration: 180, ease: 'Sine.Out' });
    });
  }

  private createEvidenceCard(item: EvidenceItem, width: number, height: number): Phaser.GameObjects.Container {
    const container = this.add.container(0, 0);
    const paper = this.add.rectangle(0, 0, width, height, 0xd4c293, 0.98).setStrokeStyle(4, 0x8f7041, 1);
    const image = this.add.image(0, -height * 0.08, this.evidenceTextureKey(item.id));
    image.setDisplaySize(width - 20, height * 0.72);
    const label = this.add.text(0, height * 0.39, item.label, {
      fontFamily: FONT,
      fontSize: `${Math.max(18, Math.round(height * 0.09))}px`,
      color: '#261d18',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    container.add([paper, image, label]);
    container.setSize(width, height).setData('evidenceId', item.id);
    container.setInteractive(new Phaser.Geom.Rectangle(-width / 2, -height / 2, width, height), Phaser.Geom.Rectangle.Contains);
    this.input.setDraggable(container);
    return container;
  }

  private handleEvidenceKeyboard(event: KeyboardEvent, level: EvidenceOrderLevel): void {
    if (event.key === 'Tab') {
      event.preventDefault();
      this.focusedIndex = (this.focusedIndex + (event.shiftKey ? -1 : 1) + this.evidenceOrder.length) % this.evidenceOrder.length;
      const id = this.evidenceOrder[this.focusedIndex];
      const container = id ? this.evidenceContainers.get(id) : undefined;
      if (container) this.tweens.add({ targets: container, scale: 1.05, duration: 120, yoyo: true });
      return;
    }
    const direction = event.key === 'ArrowLeft' || event.key === 'ArrowUp' ? -1 : event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : 0;
    if (!direction) return;
    event.preventDefault();
    const target = Phaser.Math.Clamp(this.focusedIndex + direction, 0, this.evidenceOrder.length - 1);
    if (target === this.focusedIndex) return;
    this.evidenceOrder = swapEvidence(this.evidenceOrder, this.focusedIndex, target);
    this.focusedIndex = target;
    this.incrementMove();
    this.layoutEvidence(level);
    if (ordersMatch(this.evidenceOrder, level.puzzle.correctOrder)) this.completePuzzle();
  }

  private addFittedImage(center: Phaser.Math.Vector2, maxWidth: number, maxHeight: number): { image: Phaser.GameObjects.Image; rect: ImageRect } {
    const rect = this.calculateFitRect(center, maxWidth, maxHeight);
    const image = this.add.image(center.x, center.y, this.textureKey);
    image.setDisplaySize(rect.width, rect.height);
    return { image, rect };
  }

  private calculateFitRect(center: Phaser.Math.Vector2, maxWidth: number, maxHeight: number): ImageRect {
    const source = this.getSourceImage();
    const aspect = source.width / source.height;
    let width = maxWidth;
    let height = width / aspect;
    if (height > maxHeight) {
      height = maxHeight;
      width = height * aspect;
    }
    return { x: center.x - width / 2, y: center.y - height / 2, width, height };
  }

  private addFrame(rect: ImageRect, color = 0xb99655): void {
    this.add.rectangle(rect.x + rect.width / 2, rect.y + rect.height / 2, rect.width + 10, rect.height + 10)
      .setStrokeStyle(4, color, 0.85)
      .setDepth(6);
  }

  private addPanelLabel(rect: ImageRect, label: string): void {
    this.add.text(rect.x + 12, rect.y + 10, label, {
      fontFamily: FONT,
      fontSize: `${Math.max(18, Math.round(rect.width * 0.035))}px`,
      color: '#f4dfaa',
      backgroundColor: 'rgba(7,18,27,.78)',
      padding: { x: 10, y: 5 },
    }).setDepth(8);
  }

  private toNormalized(x: number, y: number, rect: ImageRect): { x: number; y: number } {
    return { x: (x - rect.x) / rect.width, y: (y - rect.y) / rect.height };
  }

  private pulseAt(rect: ImageRect, x: number, y: number, radius: number, color = 0xe3bf68): void {
    const ring = this.add.circle(rect.x + rect.width * x, rect.y + rect.height * y, Math.max(20, rect.width * radius), color, 0.08)
      .setStrokeStyle(5, color, 0.95)
      .setDepth(30);
    this.tweens.add({
      targets: ring,
      scale: 1.35,
      alpha: 0,
      duration: 520,
      repeat: 2,
      onComplete: () => ring.destroy(),
    });
  }

  private flashMiss(x: number, y: number): void {
    const mark = this.add.text(x, y, '×', { fontFamily: FONT, fontSize: '42px', color: '#d56b6b', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setDepth(40);
    this.tweens.add({ targets: mark, y: y - 24, alpha: 0, duration: 420, onComplete: () => mark.destroy() });
    this.callbacks.onStatus('这里没有可记录的变化，再仔细看看。', 'bad');
  }

  private incrementMove(): void {
    this.moves += 1;
    this.callbacks.onMove(this.moves);
  }

  private completePuzzle(): void {
    if (this.completed) return;
    this.completed = true;
    this.callbacks.onStatus('档案已经复原。', 'good');
    this.cameras.main.flash(420, 224, 193, 113, false);
    this.time.delayedCall(520, () => this.callbacks.onComplete());
  }

  private getSourceImage(): HTMLImageElement {
    return this.textures.get(this.textureKey).getSourceImage() as HTMLImageElement;
  }

  private tileTextureKey(value: number): string {
    return `${this.level.id}-tile-${value}`;
  }

  private evidenceTextureKey(id: string): string {
    return `${this.level.id}-evidence-${id}`;
  }

  private stripTextureKey(value: number): string {
    return `${this.level.id}-strip-${value}`;
  }

  private sequenceTextureKey(value: number): string {
    return `${this.level.id}-sequence-${value}`;
  }
}
